import {
  projectSchema,
  type Project,
  type ProjectCreateInput,
  type ProjectUpdateInput,
} from '@plane/shared';
import { z } from 'zod';

import { apiBaseUrl, apiRequest, getAccessToken } from '@/services/http-client';
import type { ImagemPreparada } from '@/modules/projects/preparar-imagem';

export function fetchProjects(): Promise<Project[]> {
  return apiRequest('/api/v1/projects', z.array(projectSchema));
}

export function createProject(input: ProjectCreateInput): Promise<Project> {
  return apiRequest('/api/v1/projects', projectSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateProject(id: string, input: ProjectUpdateInput): Promise<Project> {
  return apiRequest(`/api/v1/projects/${id}`, projectSchema, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteProject(id: string): Promise<void> {
  return apiRequest(`/api/v1/projects/${id}`, z.void(), { method: 'DELETE' });
}

export function reorderProjects(ids: string[]): Promise<void> {
  return apiRequest('/api/v1/projects/ordem', z.void(), {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export function removeProjectImage(projectId: string, imageId: string): Promise<Project> {
  return apiRequest(`/api/v1/projects/${projectId}/imagens/${imageId}`, projectSchema, {
    method: 'DELETE',
  });
}

/**
 * Envia a foto como corpo binário.
 *
 * Não passa por `apiRequest` porque este é o único lugar que manda algo que não
 * é JSON: o `content-type` é o tipo da imagem, e o corpo é o blob. Os metadados
 * vão na query, que é o que dispensa `multipart` dos dois lados.
 */
export async function uploadProjectImage(
  projectId: string,
  imagem: ImagemPreparada,
  alt: string,
): Promise<Project> {
  const busca = new URLSearchParams({
    alt,
    width: String(imagem.width),
    height: String(imagem.height),
  });

  const token = getAccessToken();

  const resposta = await fetch(`${apiBaseUrl}/api/v1/projects/${projectId}/imagens?${busca}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': imagem.blob.type,
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: imagem.blob,
  });

  const corpo: unknown = await resposta.json().catch(() => null);

  if (!resposta.ok) {
    const erro = corpo as { error?: { message?: string } } | null;

    throw new Error(erro?.error?.message ?? `Falha ao enviar a imagem (${resposta.status}).`);
  }

  return projectSchema.parse(corpo);
}
